import { useState, useEffect, useMemo } from 'react';
import { Icon, Button } from '@repo/ui';
import { dateUtil, numberUtil } from '@xtnd/web/shared';
import { DateFormat } from '@xtnd/web/shared/utils/format';
import { AppealItem, MatterInvoiceSummaryFile } from '../../types';
import { AppealStatus, WriteOffStatus } from '@repo/types';
import useAppealStatus from '../../hooks/useAppealStatus';
import { useAppealDeadline } from '../../hooks/useAppealDeadline';
import { usePreviewFileQuery } from '../../queries';
import { ArAppealStatus } from '../common';
import { calculateAgreedToPay } from '../../utils/invoice';
import { AppealFeeAdjustments } from './AppealFeeAdjustments';
import { AppealAttorneyResponses } from './AppealAttorneyResponses';
import { EditableDeadline } from './EditableDeadline';

interface AppealDetailCardProps {
  appeal: AppealItem;
  invoiceData: any; // Ideally this should be typed properly
  appealsSupportFiles: MatterInvoiceSummaryFile[];
  writeOffStatus?: WriteOffStatus;
  isDark: boolean;
}

const getBorderColor = (statusClasses: string): string => {
  const match = /border-\[#([A-Fa-f0-9]+)\]/.exec(statusClasses);
  return match ? `#${match[1]}` : '#4B5563';
};

export const AppealDetailCard = ({
  appeal,
  invoiceData,
  appealsSupportFiles,
  writeOffStatus,
  isDark,
}: AppealDetailCardProps) => {
  const [expanded, setExpanded] = useState(false);
  const [selectedFile, setSelectedFile] = useState<MatterInvoiceSummaryFile | null>(null);

  const {
    deadline,
    attyDeadline,
    isSaving,
    getTodayDate,
    getClientDeadlineDate,
    canEditDeadlines,
  } = useAppealDeadline(appeal);

  const { getAppealStatusColor } = useAppealStatus(isDark ? 'dark' : 'light');

  const borderColor = useMemo(() => {
    const statusClasses = getAppealStatusColor(appeal.appealStatus as AppealStatus);
    return getBorderColor(statusClasses);
  }, [appeal.appealStatus, getAppealStatusColor]);

  const { data: document } = usePreviewFileQuery(selectedFile?.fileId || '', {
    enabled: !!selectedFile?.fileId,
  });

  useEffect(() => {
    if (document?.data && selectedFile) {
      window.open(document.data, '_blank');
      setSelectedFile(null);
    }
  }, [document, selectedFile]);

  return (
    <div
      className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden mb-4 bg-gray-50 dark:bg-gray-900/40"
      style={{ borderLeftWidth: '4px', borderLeftColor: borderColor }}
    >
      {/* Card Header */}
      <div className="flex justify-between items-center bg-gray-100 dark:bg-gray-900 p-3 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-4">
          <ArAppealStatus status={appeal.appealStatus!} />
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setExpanded(!expanded)}
          rightIcon={<Icon name={expanded ? 'HiChevronUp' : 'HiChevronDown'} />}
          className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
        >
          {expanded ? 'Hide Attorney Responses' : 'Show Attorney Responses'}
        </Button>
      </div>

      {/* Card Body */}
      <div className="p-4 space-y-4">
        {/* 2x2 Card Layout */}
        <div className="flex gap-4 items-stretch ">
          {/* Column 1: Deadlines Card */}
          <div className="w-[200px] flex flex-col gap-4 p-4 bg-gray-100 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="space-y-4">
              <EditableDeadline
                label="Atty Response Deadline"
                isEditing={attyDeadline.isEditing}
                value={attyDeadline.current}
                editedValue={attyDeadline.edited}
                onStartEdit={attyDeadline.startEdit}
                onChange={attyDeadline.handleChange}
                onSave={attyDeadline.save}
                onClear={attyDeadline.clear}
                isSaving={isSaving}
                canEdit={canEditDeadlines}
                error={attyDeadline.error}
                minDate={getTodayDate()}
                maxDate={getClientDeadlineDate() || undefined}
              />
              <EditableDeadline
                label="Client Appeal Deadline"
                isEditing={deadline.isEditing}
                value={deadline.current}
                editedValue={deadline.edited}
                onStartEdit={deadline.startEdit}
                onChange={deadline.handleChange}
                onSave={deadline.save}
                onClear={deadline.clear}
                isSaving={isSaving}
                canEdit={canEditDeadlines}
                error={deadline.error}
                minDate={getTodayDate()}
              />
            </div>
          </div>

          {/* Column 2: Financials Card */}
          <div className="w-[200px] flex flex-col gap-4 p-4 bg-gray-100 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="flex justify-between gap-4">
              <div>
                <span className="text-xs text-gray-500 font-bold tracking-wider">
                  Agreed to Pay
                </span>
                <div className="text-lg font-bold text-gray-800 dark:text-gray-200">
                  {numberUtil.formatCurrency(calculateAgreedToPay(invoiceData, appeal))}
                </div>
              </div>
            </div>
            <div>
              <span className="text-xs text-gray-500 font-bold tracking-wider">
                Total Agreed to Pay
              </span>
              <div className="text-lg font-bold text-gray-800 dark:text-gray-200">
                {numberUtil.formatCurrency(appeal.collectedAmount || 0)}
              </div>
            </div>
          </div>

          {/* Column 3: Fee Adjustments Card */}
          <div className="w-1/2 flex flex-col gap-2 p-4 bg-gray-100 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
            <AppealFeeAdjustments appeal={appeal} />
          </div>

          {/* Column 4: Comments Card */}
          <div className="w-1/4 flex flex-col gap-2 p-4 bg-gray-100 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2">
              <Icon name="HiChatAlt" className="text-gray-500" />
              <span className="text-xs  text-gray-500 font-bold tracking-wider">COMMENTS</span>
            </div>
            <div className="p-3 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/50 min-h-[120px] text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap flex-1">
              {appeal.comment || (
                <span className="text-gray-400 dark:text-gray-600 italic">No comments</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Card Footer */}
      <div className="px-4 py-2 bg-gray-100 dark:bg-gray-900/50 border-t border-gray-200 dark:border-gray-700 flex flex-wrap gap-4 text-xs text-gray-500">
        <div className="flex items-center gap-1">
          <Icon name="HiInformationCircle" className="text-gray-400 dark:text-gray-600" />
          <span>
            Created by{' '}
            <span className="text-gray-600 dark:text-gray-400">{appeal.createdBy || 'System'}</span>{' '}
            on{' '}
            {appeal.createdAt
              ? dateUtil?.formatDate(new Date(appeal.createdAt), DateFormat.US)
              : 'N/A'}
          </span>
        </div>
        {appeal.updatedBy && (
          <div className="flex items-center gap-1 pl-4 border-l border-gray-200 dark:border-gray-700">
            <span>
              Updated by{' '}
              <span className="text-gray-600 dark:text-gray-400">{appeal.updatedBy}</span> on{' '}
              {appeal.updatedAt
                ? dateUtil?.formatDate(new Date(appeal.updatedAt), DateFormat.US)
                : 'N/A'}
            </span>
          </div>
        )}
      </div>

      {/* Expanded Content */}
      {expanded && (
        <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/20">
          <AppealAttorneyResponses
            appeal={appeal}
            appealsSupportFiles={appealsSupportFiles}
            writeOffStatus={writeOffStatus}
          />
        </div>
      )}
    </div>
  );
};
